export const StorageKeys = {
  DASHBOARD_SCHEMA: '_ds.0.5.2_',
  DASHBOARD_SCHEMA_SHARED: '_dss.0.3_',
  BLOCK_SCHEMA: '_mssh',
  BLOCK_UID: '_bid',
  FETCH_QUERY: '_fq',
  FETCH_STAT_QUERY: '_fsq'
}

export const ObsoleteStorageKeys = [
  '_ds_',
  '_ds2_',
  '_ds3_',
  '_ds4_',
  '_ds5_',
  '_ds.0.1_',
  '_ds.0.2_',
  '_dss.0.1_',
  '_ds.0.3_',
  '_ds.0.4_',
  '_ds.0.5_',
  '_ds.0.5.1_',
  '_dss.0.2_'
]

/**
 * Parses a JSON string, returning the fallback value if the string cannot be parsed.
 *
 * @param {string} jsonString
 * @param {?T} fallback
 * @returns {T|undefined}
 * @template T
 */
export function parseJSON(jsonString, fallback) {
  try {
    return JSON.parse(jsonString)
  } catch {
    return fallback
  }
}

/**
 * S
 * @param {*} input
 * @returns
 */
export function stringifyToJSON(input) {
  // console.log('_')
  if (input && input === Object(input) && 'toJS' in input) {
    return JSON.stringify(input.toJS())
  }
  return JSON.stringify(input, null, (_k, v) => {
    if (v && v === Object(v) && 'toJS' in v) {
      return v.toJS()
    }
    return v
  })
}

/**
 * Stores a value in the browser's local storage (or session storage if specified),
 * stringifying it as JSON.
 *
 * @param {string§} key
 * @param {T} value
 * @param {?Storage} storage
 * @template T
 */
export const storeValue = (key, value, storage = window.localStorage) => {
  storage.setItem(key, stringifyToJSON(value))
}

/**
 * Retrieves a value stored in the browser's local storage (or session storage if specified),
 * and parses it as JSON. If the value is not found, or cannot be parsed, the fallback value
 * is returned.
 *
 * @param {string} key
 * @param {?T} fallback
 * @param {?Storage} storage
 * @returns {undefined|T}
 * @template T
 */
export const retrieveValue = (key, fallback, storage = window.localStorage) => parseJSON(storage.getItem(key), fallback)

/**
 * Removes a value from the browser's local storage (or session storage if specified).
 *
 * @param {string} key
 * @param {?Storage} storage
 */
export const removeValue = (key, storage = window.localStorage) => {
  storage.removeItem(key)
}

/**
 *
 * @param {string} key
 * @param {?T} fallback
 * @param {?Storage} storage
 * @returns {undefined|T}
 * @template T
 */
export const popStoredValue = (key, fallback, storage = window.localStorage) => {
  const value = retrieveValue(key, fallback, storage)
  storage.removeItem(key)
  return value
}

/**
 *
 * @param {string} key
 * @param {string|number} subKey
 * @param {T} value
 * @param {?Storage} storage
 * @template T extends ({}| { toJS: () => {} })
 */
export const storeKeyedValue = (key, subKey, value, storage = window.localStorage) => {
  const stored = retrieveValue(key, {}, storage) || {}
  stored[subKey] = 'toJS' in value ? value.toJS() : value
  storeValue(key, stored, storage)
}

/**
 *
 * @param {string} key
 * @param {string|number} subKey
 * @param {?T} fallback
 * @param {?Storage} storage
 * @returns {undefined|T}
 * @template T
 */
export const retrieveKeyedValue = (key, subKey, fallback, storage = window.localStorage) => {
  const stored = retrieveValue(key, {}, storage) || {}
  return stored[subKey] ?? fallback
}

/**
 *
 * @param {string} key
 * @param {null|function(T):boolean} filter
 * @param {T} value
 * @param {Storage} storage
 * @returns {boolean} If new value is different from the old value
 * @template T
 */
export const storeMergedValue = (key, filter, value, storage = window.localStorage) => {
  const stored = retrieveValue(key, {}, storage)
  if (stored && (!filter || filter(stored))) {
    if (!value || value !== Object(value)) {
      return false
    }
    const valueToMerge = 'toJS' in value ? value.toJS() : value
    const merged = { ...stored, ...valueToMerge }
    storeValue(key, merged, storage)

    // 'development' === process.env.NODE_ENV &&
    //   JSON.stringify(merged) !== JSON.stringify(stored) &&
    //   console.log(`Key: ${key} Was:`, JSON.stringify(stored, null, 2), 'Now:', JSON.stringify(merged, null, 2))
    return JSON.stringify(merged) !== JSON.stringify(stored)
  }
  if (!value || value !== Object(value)) {
    storeValue(key, null, storage)
    return false
  }
  storeValue(key, value, storage)
  return true
  // console.log(`Stored ${key} value`, retrieveValue(key, null))
}

/**
 * @param {function(T, R):T} merger
 * @returns {function(string, string|number, R, ?Storage):boolean}
 * @template T
 * @template R
 */
export const storeKeyedValueWithMerger =
  merger =>
  (key, subKey, value, storage = window.localStorage) => {
    const stored = retrieveKeyedValue(key, subKey, {}, storage)
    const merged = merger(stored, value)
    storeKeyedValue(key, subKey, merged, storage)
    return JSON.stringify(merged) !== JSON.stringify(stored)
  }

const lsCache = {
  read: key => retrieveValue(key, null),
  write: (key, value) => storeMergedValue(key, null, value)
}

/**
 * Uses the provided cache to store and retrieve the result of a promised result creator.
 * @param {string} categoryKey
 * @param {T} requestParams
 * @param {({():Promise<R>})} promisedResultCreator
 * @param {({ read: ({(string): unknown}), write:(string, unknown): void})} cache
 * @returns {R}
 * @template {T}
 * @template {R}
 */
export const employCache = async (categoryKey, requestParams, promisedResultCreator, cache = lsCache) => {
  const key = JSON.stringify(requestParams)
  const cached = cache.read(categoryKey)
  if (cached && key in cached) {
    return cached[key]
  }
  const result = await promisedResultCreator()
  cache.write(categoryKey, { [key]: result })
  return result
}

/**
 * Invalidates the dashboard cache by removing the block schema, block UID, and fetch query from LS
 */
export function invalidateDashboardCache() {
  removeValue(StorageKeys.BLOCK_SCHEMA)
  removeValue(StorageKeys.BLOCK_UID)
  removeValue(StorageKeys.FETCH_QUERY)
}

export function cleanDashboardSchema() {
  removeValue(StorageKeys.DASHBOARD_SCHEMA)
  removeValue(StorageKeys.DASHBOARD_SCHEMA_SHARED)
  invalidateDashboardCache()
}
